import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  Typography,
  Button,
  Select,
  FormControl,
  TextField,
  MenuItem,
  Switch,
} from "@mui/material";
import DateWithClear from "Components/UI/Form/DateWithClear";
import { updateNextEvent } from "API/swesum";
import dayjs from "dayjs";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { EVENT_OPTIONS } from "utils/options/EVENT_OPTIONS";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: 4,
  width: "100&",
  height: "100%",
};

export default function NextEventModal({
  invoice,
  open,
  upcomingEvents,
  setInvoice,
  onClose,
}) {
  const [nextEvent, setNextEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [description, setDescription] = useState("");
  const [interestStop, setInterestStop] = useState(false);
  const [debitClient, setDebitClient] = useState(false);
  const [error, setError] = useState("");
  const [reminderAccounts, setReminderAccounts] = useState([]);
  const { openSuccess, openError } = usePopupNotificationContext();

  const handleConfirm = async () => {
    const now = dayjs();
    const date = dayjs(selectedDate);

    const inkassoOption = EVENT_OPTIONS.find(
        (option) => option.value === "COLLECTION"
    );
    setError("");
    if (nextEvent != inkassoOption.id && date.isBefore(now, "day")) {
      setError("Valt datum får inte vara tidigare än idag.");
      return;
    }

    try {
      const body = {
        newEvent: nextEvent,
        date: dayjs(date).format("YYYY-MM-DD"),
        interestStop: interestStop,
        description: description,
        DebitClient: debitClient,
      };
      const res = await updateNextEvent(invoice.Id, body);

      if (res) {
        setInvoice(res.data.Data);
        openSuccess("Nästa händelse uppdaterad");
      }
    } catch (error) {
      openError(`Nästa händelse kunde ej uppdateras, ${error.response.data}`);
    }
    onClose();
  };

  useEffect(() => {
    getWriteOffAccounts();
    setNextEvent(upcomingEvents[0]?.id);
    setSelectedDate(dayjs(upcomingEvents[0]?.date).format("YYYY-MM-DD"));
    setDescription("");
    setInterestStop(false);
    setDebitClient(false);
    setError("");
  }, [open]);

  //sketchy function to check if changing to selected event will result a write off of existing reminder accounts
  const getWriteOffAccounts = () => {
    if (!invoice) return;

    const payruleConditions = [];

    if (nextEvent === 1) {
      payruleConditions.push("PÅMINNELSE1");
      payruleConditions.push("PÅMINNELSE2");
    }

    if (nextEvent === 2) {
      payruleConditions.push("PÅMINNELSE2");
    }

    const reminderArticles = invoice?.EndCustomerArticles.filter((article) =>
      payruleConditions.includes(article.Payrule)
    );

    const reminderAccountsToWriteOff = invoice?.EndCustomerAccounts.filter(
      (account) => {
        const relatedArticle = reminderArticles.find(
          (article) =>
            article.Id === account.EndCustomerArticleId &&
            account.SystemStatus === "1" &&
            account.WriteOffId == null
        );
        if (relatedArticle) {
          account.label =
            relatedArticle.Payrule === "PÅMINNELSE1"
              ? "Påminnelse 1"
              : "Påminnelse 2";
          return true;
        }
        return false;
      }
    );

    setReminderAccounts(reminderAccountsToWriteOff);
  };

  useEffect(() => {
    if (nextEvent && upcomingEvents.length > 0) {
      const newDate = upcomingEvents?.find(
        (event) => event?.id === nextEvent
      )?.date;

      setSelectedDate(dayjs(newDate).format("YYYY-MM-DD"));
    }
  }, [nextEvent]);

  useEffect(() => {
    if (upcomingEvents.length > 0) {
      setNextEvent(upcomingEvents[0].id);
      setSelectedDate(dayjs(upcomingEvents[0].date).format("YYYY-MM-DD"));
    }
  }, [upcomingEvents]);

  useEffect(() => {
    if (!nextEvent) return;
    getWriteOffAccounts();
  }, [nextEvent]);

  useEffect(() => {}, [reminderAccounts]);

  return (
    <>
      <Dialog open={open}>
        <Box sx={style}>
          <Typography variant="h5" fontWeight={"bold"}>
            Justera Nästa Händelse
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            width={"100%"}
          >
            <FormControl fullWidth>
              <Box display="flex" gap={3} flexDirection="column">
                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Nästa händelse:
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={nextEvent}
                      size={"small"}
                      onChange={(e) => setNextEvent(e.target.value)}
                    >
                      {EVENT_OPTIONS.map((event) => (
                        <MenuItem key={event.value} value={event.id}>
                          {event.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  display="flex"
                  justifyContent={"center"}
                  alignItems="center"
                >
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Datum:
                  </Typography>
                  <DateWithClear
                    onChange={(value) => {
                      setSelectedDate(dayjs(value).format("YYYY-MM-DD"));
                    }}
                    value={dayjs(selectedDate)}
                  />
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={"flex-start"}
                  flexDirection="row"
                  gap={2}
                >
                  <Typography fontWeight={"medium"} fontSize={16}>
                    Räntestopp
                  </Typography>
                  <Switch
                    checked={interestStop}
                    onChange={(e) => {
                      setInterestStop(e.target.checked);
                    }}
                  />
                </Box>
                {reminderAccounts.length > 0 ? (
                  <Box display="flex" flexDirection={"column"} gap={2}>
                    <Typography fontWeight={"medium"} fontSize={16}>
                      Följande avgifter kommer att skrivas av:
                    </Typography>
                    <Box>
                      {reminderAccounts.map((account) => {
                        return (
                          <Box
                            display="flex"
                            flexDirection="row"
                            marginBottom={2}
                            gap={0}
                          >
                            {" "}
                            <Typography
                              fontWeight={"medium"}
                              fontSize={14}
                              width={"50%"}
                            >
                              {account.label}
                            </Typography>
                            <Typography
                              fontSize={14}
                            >{`${account.Amount}:-`}</Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    <Typography
                      fontWeight={"medium"}
                      fontSize={16}
                      width={"50%"}
                    >
                      Debiteras avgifter:
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        defaultValue={false}
                        onChange={(e) => setDebitClient(e.target.value)}
                      >
                        <MenuItem value={false}>Swesum</MenuItem>
                        <MenuItem value={true}>Klient</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  gap={2}
                >
                  <Typography
                    fontWeight={"medium"}
                    fontSize={16}
                    width={"100%"}
                  >
                    Notering:
                  </Typography>
                  <TextField
                    minRows={4}
                    placeholder="Notering"
                    multiline
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{
                      minWidth: 450,
                      width: "100%",
                      borderRadius: 3,
                      border: "1px solid gray",
                    }}
                  />
                </Box>
              </Box>
            </FormControl>
          </Box>
          <Box>
            <Typography fontSize={11} color={"error"}>
              {error}
            </Typography>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={5}
            justifyContent={"space-around"}
            height={55}
            width={"100%"}
          >
            <Button
              sx={{
                backgroundColor: "transparent",
                outline: "1px solid #1976d2",
                color: "#1976d2",
                width: "100%",
              }}
              onClick={() => onClose()}
            >
              Avbryt
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              sx={{ backgroundColor: "#1976d2", color: "white", width: "100%" }}
            >
              Bekräfta
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
